<script lang="ts">
	import { goto } from '$app/navigation';
	import { cfgToThemeCdnPath } from '@infinitro/config';
	import { t, Verbiage as VerbiageKeys } from '@infinitro/content';
	import { isMobile, toSiteUrl } from '@infinitro/global';

	type BannerCardVariant = 'primary' | 'secondary';
	type Verbiage = (typeof VerbiageKeys)[keyof typeof VerbiageKeys];

	interface BannerCardConfig {
		id: string;
		variant: BannerCardVariant;
		href: string;
		imageName: string;
		desktopTitle: Verbiage;
		mobileTitle?: Verbiage;
		descriptionLine1: Verbiage;
		descriptionLine2: Verbiage;
		cta: Verbiage;
	}

	export let cards: readonly BannerCardConfig[] = [];

	const toBannerImageUrl = (imageName: string, mobile: boolean) => {
		return cfgToThemeCdnPath(`/home-page/${imageName}-${mobile ? 'm' : 'd'}.png`);
	};

	const clickIt = (relativeUrl: string) => {
		goto(toSiteUrl(relativeUrl));
	};
</script>

<div class="container-d">
	{#each cards as card (card.id)}
		<div class={card.variant === 'primary' ? 'primary-d-card' : 'secondary-d-card'}>
			<div
				class={card.variant === 'primary' ? 'primary-d-card-content' : 'secondary-d-card-content'}
			>
				<div class={card.variant === 'primary' ? 'primary-d-card-text' : 'secondary-d-card-text'}>
					<div
						class={card.variant === 'primary' ? 'primary-d-card-title' : 'secondary-d-card-title'}
					>
						{$t(card.desktopTitle)}
					</div>
					<div class={card.variant === 'primary' ? 'primary-d-card-desc' : 'secondary-d-card-desc'}>
						{$t(card.descriptionLine1)} <br />
						{$t(card.descriptionLine2)}
					</div>
				</div>
				<button
					class={card.variant === 'primary' ? 'primary-d-card-btn' : 'secondary-d-card-btn'}
					on:click={() => clickIt(card.href)}
				>
					<div
						class={card.variant === 'primary'
							? 'primary-d-card-btn-text'
							: 'secondary-d-card-btn-text'}
					>
						{$t(card.cta)}
					</div>
				</button>
			</div>
			<img
				class={card.variant === 'primary' ? 'primary-d-card-img' : 'secondary-d-card-img'}
				src={toBannerImageUrl(card.imageName, $isMobile)}
				alt=""
			/>
		</div>
	{/each}
</div>

<div class="container-m">
	{#each cards as card (card.id)}
		<button
			class={card.variant === 'primary' ? 'card1-m' : 'card2-m'}
			on:click={() => clickIt(card.href)}
		>
			<div class={card.variant === 'primary' ? 'card1-m-content' : 'card2-m-content'}>
				<div class="card-m-lead">
					<div class={card.variant === 'primary' ? 'card1-m-title' : 'card2-m-title'}>
						{$t(card.mobileTitle ?? card.desktopTitle)}
					</div>
					<div class={card.variant === 'primary' ? 'card1-m-desc' : 'card2-m-desc'}>
						{$t(card.descriptionLine1)}<br />
						{$t(card.descriptionLine2)}
					</div>
				</div>
				<div class={card.variant === 'primary' ? 'card1-m-btn' : 'card2-m-btn'}>{$t(card.cta)}</div>
			</div>
			<img
				class={card.variant === 'primary' ? 'img1-m' : 'img2-m'}
				src={toBannerImageUrl(card.imageName, $isMobile)}
				alt=""
			/>
		</button>
	{/each}
</div>

<style>
	/* desktop */
	.container-d {
		font-weight: var(--font-main);
		margin-top: 3rem;
		margin-bottom: 1rem;
		display: none;
		height: 206px;
		width: 100%;
		align-items: center;
		justify-content: flex-start;
		gap: 1.5rem;
		align-self: stretch
}
	@media (min-width: 768px) {
		.container-d {
				display: inline-flex
		}
}

	.primary-d-card {
		position: relative;
		flex: 1 1 0%;
		align-self: stretch;
		border-radius: 1rem;
		background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
		--tw-gradient-from: var(--component-home-page-text-banner02-card1-bg-from) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: var(--component-home-page-text-banner02-card1-bg-to) var(--tw-gradient-to-position)
}

	.primary-d-card-content {
		position: absolute;
		left: 32px;
		top: 36px;
		display: inline-flex;
		width: 297px;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 0.75rem
}

	.primary-d-card-text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 0.25rem;
		align-self: stretch
}

	.primary-d-card-title {
		justify-content: flex-start;
		align-self: stretch;
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 700;
		line-height: 26px;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1))
}

	.primary-d-card-desc {
		justify-content: flex-start;
		align-self: stretch;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		line-height: 22px;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		opacity: 0.7
}

	.primary-d-card-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		border-radius: 0.75rem;
		background-color: var(--component-home-page-text-banner02-btn1-bg);
		padding: 1rem
}

	.primary-d-card-btn-text {
		justify-content: flex-start;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1))
}

	.primary-d-card-img {
		position: absolute;
		bottom: 0px;
		right: 0px;
		height: 257px;
		width: 411px;
		border-bottom-right-radius: 1rem
}

	.secondary-d-card {
		position: relative;
		flex: 1 1 0%;
		align-self: stretch;
		border-radius: 1rem;
		background-image: linear-gradient(to left, var(--tw-gradient-stops));
		--tw-gradient-from: var(--component-home-page-text-banner02-card2-bg-from) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: var(--component-home-page-text-banner02-card2-bg-to) var(--tw-gradient-to-position)
}

	.secondary-d-card-content {
		position: absolute;
		left: 32px;
		top: 36px;
		display: inline-flex;
		width: 289px;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 0.75rem
}

	.secondary-d-card-text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 0.25rem;
		align-self: stretch
}

	.secondary-d-card-title {
		justify-content: flex-start;
		align-self: stretch;
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 700;
		line-height: 26px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.secondary-d-card-desc {
		justify-content: flex-start;
		align-self: stretch;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		line-height: 22px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		opacity: 0.7
}

	.secondary-d-card-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		border-radius: 0.75rem;
		background-color: var(--component-home-page-text-banner02-btn2-bg);
		padding: 1rem
}

	.secondary-d-card-btn-text {
		justify-content: flex-start;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1rem;
		color: #ad2941
}

	.secondary-d-card-img {
		position: absolute;
		bottom: 0px;
		right: 0px;
		height: 240px;
		width: 306px;
		border-bottom-right-radius: 1rem
}

	/* mobile */
	.container-m {
		font-weight: var(--font-main);
		display: inline-flex;
		width: 100%;
		flex-direction: column;
		align-items: stretch;
		justify-content: space-around;
		gap: 1rem;
		align-self: stretch
}
	@media (min-width: 768px) {
		.container-m {
				display: none
		}
}
	.container-m {
		margin-bottom: 1rem
}

	.card1-m {
		position: relative;
		height: 234px;
		align-self: stretch;
		text-align: left;
		background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
		--tw-gradient-from: var(--component-home-page-text-banner02-card1-bg-from) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: var(--component-home-page-text-banner02-card1-bg-to) var(--tw-gradient-to-position);
		overflow: hidden;
		border-radius: 1rem
}

	.card-m-lead {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.25rem
}

	.card1-m-content {
		position: absolute;
		left: 20px;
		top: 20px;
		bottom: 20px;
		z-index: 10;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-around
}

	.card1-m-title {
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 700;
		line-height: 26px;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1))
}

	.card1-m-desc {
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		line-height: 22px;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		opacity: 0.7
}

	.card1-m-btn {
		border-radius: 0.75rem;
		background-color: var(--component-home-page-text-banner02-btn1-bg);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1))
}

	.card2-m {
		position: relative;
		height: 234px;
		align-self: stretch;
		text-align: left;
		background-image: linear-gradient(to left, var(--tw-gradient-stops));
		--tw-gradient-from: var(--component-home-page-text-banner02-card2-bg-from) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: var(--component-home-page-text-banner02-card2-bg-to) var(--tw-gradient-to-position);
		overflow: hidden;
		border-radius: 1rem
}

	.card2-m-content {
		position: absolute;
		left: 20px;
		top: 20px;
		bottom: 20px;
		z-index: 10;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-around
}

	.card2-m-title {
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 700;
		line-height: 26px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.card2-m-desc {
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		line-height: 22px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		opacity: 0.7
}

	.card2-m-btn {
		border-radius: 0.75rem;
		background-color: var(--component-home-page-text-banner02-btn2-bg);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1rem;
		color: #ad2941
}

	.img1-m {
		position: absolute;
		right: 0px;
		bottom: 0px;
		height: 234px;
		width: auto;
		border-bottom-right-radius: 1rem;
		-o-object-fit: contain;
		   object-fit: contain
}

	.img2-m {
		position: absolute;
		right: 0px;
		bottom: 0px;
		height: 234px;
		width: auto;
		border-bottom-right-radius: 1rem;
		-o-object-fit: contain;
		   object-fit: contain
}
</style>
